import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import _unescape from 'lodash/unescape';

import {
  MassMessage,
  MassMessageStatusCode,
  MassMessageBuilding,
  MassMessageRolesGroup,
  MassMessageRole,
} from 'models';
import { RecipientsBody } from 'services/communication-messages';

import { customFormat } from 'utils';
import safeParseHtml from 'shared/utils/safeParseHtml';

import { FileLoader, EntityType } from 'features/file-loader';

import MassMessageCreateForm from 'containers/mass-message-create-form';
import { PageTitle, Recipients } from 'containers';
import {
  PageLoader,
  Error,
  NoData,
  TitledBox,
  Flex,
  ButtonShape,
  Loader,
  Modal,
  CaseTitle,
  Checkbox,
} from 'components';
import SpacesSelectionList from 'components/spaces-selection-list';
import RolesSelectionList from 'components/roles-selection-list';
import MassMessageGeneralInfo from './mass-message-general-info';
import CheckIcon from '@material-ui/icons/Check';
import MissingRecipientsNotification from './missing-recipients-notification';

import { InfoValue } from 'components/styled-components';

import clsx from 'clsx';
import { useStyles, useActionToolbarStyles } from './style';

interface MassMessageDetailsProps {
  buildings: MassMessageBuilding[];
  hasRecipients: boolean;
  isLoading: boolean;
  isError: boolean;
  isUploading: boolean;
  communicationMessage: MassMessage | null;
  showSuccessDialog: boolean;
  rolesEntities: (MassMessageRole | MassMessageRolesGroup)[];
  onClose: () => void;
  onCloseSuccessModal: () => void;
  onDistribute: () => void;
  onRefresh: () => void;
}

interface ActionToolbarProps {
  isUploading: boolean;
  onDistribute: () => void;
  onOpenEditForm: () => void;
}

const ActionToolbar = ({
  isUploading,
  onDistribute,
  onOpenEditForm,
}: ActionToolbarProps) => {
  const classes = useActionToolbarStyles();

  const { t } = useTranslation();

  return (
    <div className={classes.btnsWrapper}>
      <ButtonShape
        className={classes.btn}
        onClick={onDistribute}
        disabled={isUploading}
      >
        {t('#portal.massmessage.button.distribute')}

        {isUploading ? (
          <Loader size={18} style={{ display: 'block', marginLeft: '10px' }} />
        ) : null}
      </ButtonShape>
      <ButtonShape
        onClick={onOpenEditForm}
        className={classes.btn}
        disabled={isUploading}
      >
        {t('#portal.massmessage.button.edit')}
      </ButtonShape>
    </div>
  );
};

const MassMessageDetails = ({
  hasRecipients,
  buildings,
  communicationMessage,
  isLoading,
  isError,
  isUploading,
  showSuccessDialog,
  rolesEntities,
  onClose,
  onCloseSuccessModal,
  onDistribute,
  onRefresh,
}: MassMessageDetailsProps) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [openEditForm, setOpenEditForm] = useState(false);
  const [
    isShownMissingRecipientsNotification,
    showMissingRecipientsNotification,
  ] = useState(false);

  const recipientsBody: RecipientsBody = useMemo(
    () => ({
      communicationMessageId: communicationMessage?.Id ?? null,
      coopId: communicationMessage?.Cooperative?.Id ?? null,
      distributingList: null,
      selectedRoles: communicationMessage?.RecipientRoles ?? [],
      selectedSpaces: communicationMessage?.Spaces ?? [],
      sendEmail: communicationMessage?.SendEmail ?? false,
      sendFloorApp: communicationMessage?.SendFloorApp ?? false,
      sendPortalMessage: communicationMessage?.SendPortalMessage ?? false,
      sendSms: communicationMessage?.SendSms ?? false,
    }),
    [communicationMessage]
  );

  const handleOpenEditForm = () => {
    setOpenEditForm(true);
  };

  const handleHideMissingRecipientsNotification = () => {
    showMissingRecipientsNotification(false);
  };

  const handleDistributeMessage = () => {
    if (hasRecipients) {
      onDistribute();
    } else {
      showMissingRecipientsNotification(true);
    }
  };

  const handleCloseEditForm = useCallback(
    (update?: boolean) => {
      setOpenEditForm(false);

      if (update) {
        onRefresh();
      }
    },
    [onRefresh]
  );

  if (isLoading) return <PageLoader />;
  if (isError) return <Error />;
  if (!communicationMessage) return <NoData />;

  const {
    Id,
    PortalMessageDue,
    RecipientRoles,
    SendEmail,
    SendPortalMessage,
    SendSms,
    Spaces,
    Status,
    Subject,
  } = communicationMessage;

  const showActionToolbar = Status?.Value === MassMessageStatusCode.Draft;

  return (
    <>
      <div className={classes.topBar}>
        <PageTitle />
        {showActionToolbar && (
          <ActionToolbar
            isUploading={isUploading}
            onDistribute={handleDistributeMessage}
            onOpenEditForm={handleOpenEditForm}
          />
        )}
      </div>
      <Flex className={classes.flex}>
        <TitledBox
          cls={{
            boxWrapper: classes.titledBox,
          }}
          title={'G_GeneralInformation'}
        >
          <CaseTitle
            title={Subject && _unescape(Subject)}
            status={Status?.Label || undefined}
          />
          <MassMessageGeneralInfo massMessageData={communicationMessage} />
        </TitledBox>
        <TitledBox
          cls={{
            boxWrapper: classes.titledBox,
            box: classes.fileloaderBox,
          }}
          title={'Kuvaus'}
        >
          <InfoValue
            style={{
              width: '100%',
              textAlign: 'left',
              justifyContent: 'flex-start',
              whiteSpace: 'pre-wrap',
            }}
          >
            {communicationMessage.Description &&
              safeParseHtml(_unescape(communicationMessage.Description))}
          </InfoValue>
        </TitledBox>
        <TitledBox
          cls={{
            boxWrapper: classes.titledBox,
            box: classes.fileloaderBox,
          }}
          title={'Liitteet'}
        >
          <FileLoader
            readonly
            entityId={Id}
            entityType={EntityType.MassMessage}
          />
        </TitledBox>
        <TitledBox
          cls={{
            boxWrapper: classes.titledBox,
            box: clsx(classes.p10, classes.row),
          }}
          title={'Vastaanottajien valinta ja lähetystapa'}
        >
          <div className={clsx(classes.flex, classes.recepientsWrapper)}>
            <SpacesSelectionList
              disabled
              buildings={buildings}
              selectedSpaces={Spaces}
            />
          </div>
          <div
            className={clsx(
              classes.flex,
              classes.max50,
              classes.recepientsWrapper
            )}
          >
            <RolesSelectionList
              disabled
              rolesEntities={rolesEntities}
              selectedRoles={RecipientRoles}
            />
          </div>
          <div className={clsx(classes.flex, classes.column)}>
            <ul
              className={clsx(
                classes.sendCheckboxUl,
                classes.ml20,
                classes.checkboxList
              )}
            >
              <li className={classes.checkboxField}>
                <Checkbox checked={!!SendEmail} disabled />
                <span className={classes.checkboxLabel}>
                  {t('##attribute#uds_communicationmessage#uds_isemail')}
                </span>
              </li>
              <li className={classes.checkboxField}>
                <Checkbox checked={!!SendPortalMessage} disabled />
                <span className={classes.checkboxLabel}>
                  {t(
                    '##attribute#uds_communicationmessage#uds_isportalmessage'
                  )}{' '}
                  {SendPortalMessage && PortalMessageDue
                    ? `(näkyyvyys päättyy ${customFormat(
                        new Date(PortalMessageDue)
                      )})`
                    : null}
                </span>
              </li>
              {/* <li className={classes.checkboxField}>
                <Checkbox checked={!!SendFloorApp} disabled />
                <span className={classes.checkboxLabel}>
                  {t('##attribute#uds_communicationmessage#uds_showinfloorapp')}{' '}
                  {SendFloorApp && FloorAppMessageDue
                    ? `(näkyyvyys päättyy ${customFormat(
                        new Date(FloorAppMessageDue)
                      )})`
                    : null}
                </span>
              </li> */}
              <li className={classes.checkboxField}>
                <Checkbox disabled checked={!!SendSms} />
                <span className={classes.checkboxLabel}>Tekstiviesti</span>
              </li>
            </ul>
          </div>
        </TitledBox>
      </Flex>
      <Flex className={classes.flex}>
        <TitledBox
          cls={{
            boxWrapper: classes.titledBox,
            box: classes.p10,
          }}
          title={'#portal.massmessage.section.recipients'}
        >
          <Recipients recipientsBody={recipientsBody} />
        </TitledBox>
      </Flex>
      {openEditForm ? (
        <MassMessageCreateForm
          communicationMessageId={Id}
          method="update"
          handleModalClose={handleCloseEditForm}
        />
      ) : null}
      <Modal
        open={showSuccessDialog}
        maxWidth="sm"
        fullWidth
        handleClose={onClose}
      >
        <div className={classes.successContainer}>
          <div className={classes.checkIconWrapper}>
            <CheckIcon className={classes.checkIcon} />
          </div>
          <p className={classes.successTitle}>
            {t('#portal.massmessage.message.destributed')}
          </p>
          <ButtonShape className={classes.btn} onClick={onCloseSuccessModal}>
            {t('#portal.massmessage.button.return')}
          </ButtonShape>
        </div>
      </Modal>
      <Modal
        open={isShownMissingRecipientsNotification}
        maxWidth="xs"
        fullWidth
        handleClose={handleHideMissingRecipientsNotification}
      >
        <MissingRecipientsNotification />
      </Modal>
    </>
  );
};

export default MassMessageDetails;
